import api from '../apiSingleton';

const MEASUREMENT_ID = 'G-WVX6V8TYFN';
const CONTAINER_ID = MEASUREMENT_ID.replace('G-', '');

export function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);

    if (parts.length === 2) return parts.pop().split(';').shift();

return null;
}

export function getGAIdentifiers() {
    const gaCookie = getCookie('_ga');

    let ga_client_id = null;

    if (gaCookie) {
        const parts = gaCookie.split('.');

        if (parts.length >= 4) {
            ga_client_id = `${parts[2]}.${parts[3]}`;
        }
    }

    const gaSessionCookie = getCookie(`_ga_${CONTAINER_ID}`);

    let ga_session_id = null;

    let ga_session_number = null;

    if (gaSessionCookie) {
        const parts = gaSessionCookie.split('.');

        if (parts.length >= 5) {
            ga_session_id = parts[2];
            ga_session_number = parts[3];
        }
    }

    return { ga_client_id, ga_session_id, ga_session_number };
}

export const pushToDataLayer = (eventName, ecommerceData) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });

    const identifiers = getGAIdentifiers();
    const payload = {
        event     : eventName,
        ecommerce : ecommerceData
    };

    if (identifiers.ga_client_id) payload.ga_client_id = identifiers.ga_client_id;
    if (identifiers.ga_session_id) payload.ga_session_id = identifiers.ga_session_id;
    if (identifiers.ga_session_number) payload.ga_session_number = identifiers.ga_session_number;

    window.dataLayer.push(payload);
};

export const enrichItemsWithDetails = async (items, cityId = 1) => {
    if (!items || items.length === 0) return [];

    const enrichedItems = await Promise.all(items.map(async (item) => {
        try {
            const productId = item.item_id || item.productId || item.id;

            if (!productId) return item;

            const productDetail = await api.products.list(productId, { cityId });

            return {
                ...item,
                item_id       : productId.toString(),
                item_name     : productDetail?.nameUa || item.item_name || item.name || '',
                price         : productDetail?.price?.discountPrice || productDetail?.price?.price || item.price || 0,
                item_category : productDetail?.category?.name || item.item_category || '',
                item_brand    : productDetail?.producer || item.item_brand || 'Farmacia'
            };
        } catch (e) {
            console.log(`[GA4] Failed to enrich product ${item.item_id || item.productId}:`, e);

return {
                ...item,
                item_id   : (item.item_id || item.productId || item.id)?.toString(),
                item_name : item.item_name || item.name || '',
                price     : item.price || 0
            };
        }
    }));


return enrichedItems;
};

export const trackViewItemList = (itemListId, itemListName, items) => {
    const formattedItems = items.map(item => ({
        ...item,
        item_id       : (item.item_id || item.productId || item.id)?.toString(),
        item_name     : item.item_name || item.nameUa || item.name || '',
        price         : item.price?.discountPrice || item.price?.price || item.price || 0,
        item_category : item.category?.name || item.item_category || '',
        item_brand    : item.producer || item.item_brand || 'Farmacia'
    }));

    const value = formattedItems.reduce((acc, item) => acc + ((item.price || 0) * (item.quantity || 1)), 0);

    pushToDataLayer('view_item_list', {
        item_list_id   : itemListId,
        item_list_name : itemListName,
        items          : formattedItems,
        currency       : 'UAH',
        value          : +value.toFixed(2)
    });
};

export const trackSelectItem = (itemListId, itemListName, item) => {
    const formattedItem = {
        ...item,
        item_id       : (item.item_id || item.productId || item.id)?.toString(),
        item_name     : item.item_name || item.nameUa || item.name || '',
        price         : item.price?.discountPrice || item.price?.price || item.price || 0,
        item_category : item.category?.name || item.item_category || '',
        item_brand    : item.producer || item.item_brand || 'Farmacia'
    };

    pushToDataLayer('select_item', {
        item_list_id   : itemListId,
        item_list_name : itemListName,
        items          : [ formattedItem ],
        currency       : 'UAH',
        value          : +((formattedItem.price || 0)).toFixed(2)
    });
};

export const trackViewItem = (item) => {
    const formattedItem = {
        ...item,
        item_id       : (item.item_id || item.productId || item.id)?.toString(),
        item_name     : item.item_name || item.nameUa || item.name || '',
        price         : item.price?.discountPrice || item.price?.price || item.price || 0,
        item_category : item.category?.name || item.item_category || '',
        item_brand    : item.producer || item.item_brand || 'Farmacia'
    };

    pushToDataLayer('view_item', {
        items    : [ formattedItem ],
        currency : 'UAH',
        value    : +((formattedItem.price || 0)).toFixed(2)
    });
};

export const trackAddToCartAsync = async (items, cityId = 1) => {
    const enrichedItems = await enrichItemsWithDetails(items, cityId);
    const value = enrichedItems.reduce((acc, item) => acc + ((item.price || 0) * (item.quantity || 1)), 0);

    pushToDataLayer('add_to_cart', {
        currency : 'UAH',
        value    : +value.toFixed(2),
        items    : enrichedItems
    });
};

export const trackRemoveFromCartAsync = async (items, cityId = 1) => {
    const enrichedItems = await enrichItemsWithDetails(items, cityId);
    const value = enrichedItems.reduce((acc, item) => acc + ((item.price || 0) * (item.quantity || 1)), 0);

    pushToDataLayer('remove_from_cart', {
        currency : 'UAH',
        value    : +value.toFixed(2),
        items    : enrichedItems
    });
};

export const trackViewCartAsync = async (items, cityId = 1) => {
    const enrichedItems = await enrichItemsWithDetails(items, cityId);
    const value = enrichedItems.reduce((acc, item) => acc + ((item.price || 0) * (item.quantity || 1)), 0);

    pushToDataLayer('view_cart', {
        currency : 'UAH',
        value    : +value.toFixed(2),
        items    : enrichedItems
    });
};

export const trackBeginCheckoutAsync = async (items, cityId = 1) => {
    const enrichedItems = await enrichItemsWithDetails(items, cityId);
    const value = enrichedItems.reduce((acc, item) => acc + ((item.price || 0) * (item.quantity || 1)), 0);

    pushToDataLayer('begin_checkout', {
        currency : 'UAH',
        value    : +value.toFixed(2),
        items    : enrichedItems
    });
};

export const trackAddShippingInfoAsync = async (items, shippingTier, cityId = 1) => {
    const enrichedItems = await enrichItemsWithDetails(items, cityId);
    const value = enrichedItems.reduce((acc, item) => acc + ((item.price || 0) * (item.quantity || 1)), 0);

    pushToDataLayer('add_shipping_info', {
        currency      : 'UAH',
        value         : +value.toFixed(2),
        shipping_tier : shippingTier,
        items         : enrichedItems
    });
};

export const trackAddPaymentInfoAsync = async (items, paymentType, cityId = 1) => {
    const enrichedItems = await enrichItemsWithDetails(items, cityId);
    const value = enrichedItems.reduce((acc, item) => acc + ((item.price || 0) * (item.quantity || 1)), 0);

    pushToDataLayer('add_payment_info', {
        currency     : 'UAH',
        value        : +value.toFixed(2),
        payment_type : paymentType,
        items        : enrichedItems
    });
};

export const trackPurchaseAsync = async (transactionId, items, cityId = 1) => {
    if (!transactionId) return;
    const dedupKey = `ga4_purchase_${transactionId}`;

    if (localStorage.getItem(dedupKey)) {
        return;
    }

    const enrichedItems = await enrichItemsWithDetails(items, cityId);
    const value = enrichedItems.reduce((acc, item) => acc + ((item.price || 0) * (item.quantity || 1)), 0);

    pushToDataLayer('purchase', {
        transaction_id : transactionId.toString(),
        value          : +value.toFixed(2),
        tax            : 0,
        shipping       : 0,
        currency       : 'UAH',
        items          : enrichedItems
    });

    localStorage.setItem(dedupKey, 'true');
};
